import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import moment from 'moment';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/**
 * AggregatedAssetExpenseChart
 * - Accepts a list of assetIds (e.g., property structures + land)
 * - Fetches maintenance-items for each asset and aggregates Actual vs Expected expenses over time
 * - Provides same look/feel as AssetExpenseChart
 */
const AggregatedAssetExpenseChart = ({ assetIds = [], chartTitle = 'Aggregated Property Expenses', extraRightControls = null, onRangeChange = null, preferAssetExpenseTypes = false, showExpected = false, onToggleExpected = null, groupByGroup = false }) => {
  const [showSubcats, setShowSubcats] = useState(false);
  const [showRecurring, setShowRecurring] = useState(false);
  const [showProjectedRecurring, setShowProjectedRecurring] = useState(false);
  const [cumulative, setCumulative] = useState(false);
  // Optional: internal toggle to override and show high-level groups (Financing, Operating, CapEx)
  const [useGroups, setUseGroups] = useState(groupByGroup);
  const rollupHome = false;

  useEffect(() => {
    setUseGroups(groupByGroup);
  }, [groupByGroup]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actuals, setActuals] = useState([]);
  const [projected, setProjected] = useState([]);
  const [aggregation, setAggregation] = useState('monthly');

  // Date range states: default YTD
  const [fromDate, setFromDate] = useState(() => {
    const now = new Date();
    // Default to last 12 months for better context, or keep Jan 1 if that's preferred. 
    // The user said they saw Jan 2025 missing, and today is Jan 2026. 
    // Jan 1 2026 would be YTD start, so Jan 2025 WOULD be missing by default.
    // Let's change default to Jan 1 of PREVIOUS year to show more history.
    const start = new Date(now.getFullYear() - 1, 0, 1);
    return start.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => {
    const now = new Date();
    const dec31 = new Date(now.getFullYear(), 11, 31);
    return dec31.toISOString().slice(0, 10);
  });

  useEffect(() => {
    const fetchAll = async () => {
      if (!assetIds || assetIds.length === 0) {
        setActuals([]);
        setProjected([]);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const results = await Promise.allSettled(
          assetIds.map((id) => axios.get(`/api/balance_sheet/assets/physical_assets/${id}/linked-expenses/`, {
            params: {
              start_date: fromDate,
              end_date: toDate,
              only_txn_backed: 1,
              // When used by the Vehicle page, include derived AssetExpense rows for EV charging (asset_input)
              include_asset_input: preferAssetExpenseTypes ? 1 : 0,
            }
          }))
        );
        const a = [];
        const p = [];
        for (const r of results) {
          if (r.status === 'fulfilled') {
            if (Array.isArray(r.value?.data?.actual)) a.push(...r.value.data.actual);
            if (Array.isArray(r.value?.data?.projected)) p.push(...r.value.data.projected);
          }
        }
        setActuals(a);
        setProjected(p);
      } catch (e) {
        setError('Failed to load aggregated expenses.');
        setActuals([]);
        setProjected([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [JSON.stringify(assetIds), fromDate, toDate, preferAssetExpenseTypes]);

  const [hiddenSubcats, setHiddenSubcats] = useState(new Set());

  useEffect(() => {
    setHiddenSubcats(new Set());
  }, [showSubcats, useGroups, showRecurring, assetIds]);

  const { labels, actualSeries, expectedSeries, actualBreakdown, expectedBreakdown, subBreakActual, subBreakExpected, subcats, dateRange, stats } = useMemo(() => {
    const dateRange = [];
    let start = moment(fromDate);
    const end = moment(toDate);
    while (start.isSameOrBefore(end)) {
      dateRange.push(start.format('YYYY-MM-DD'));
      if (aggregation === 'daily') start.add(1, 'day');
      else if (aggregation === 'weekly') start.add(1, 'week');
      else if (aggregation === 'monthly') start.add(1, 'month');
      else if (aggregation === 'yearly') start.add(1, 'year');
      else start.add(1, 'day');
    }

    if ((!actuals || actuals.length === 0) && (!projected || projected.length === 0)) {
      return {
        labels: dateRange,
        actualSeries: dateRange.map(() => 0),
        expectedSeries: dateRange.map(() => 0),
        actualBreakdown: {},
        expectedBreakdown: {},
        subBreakActual: {},
        subBreakExpected: {},
        subcats: [],
        dateRange,
        stats: { sum: 0, avg: 0, stdDev: 0 }
      };
    }

    const actualsMap = {};
    const expectedsMap = {};
    const actualBreakdown = {}; // {period: {category: sum}}
    const expectedBreakdown = {};

    const getPeriod = () => {
      if (aggregation === 'weekly') return 'week';
      if (aggregation === 'monthly') return 'month';
      if (aggregation === 'yearly') return 'year';
      return 'day';
    };

    // Normalize a category-ish string into a canonical token (lowercase, underscores)
    const canon = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    const isUtilitiesLike = (s) => {
      const c = canon(s);
      if (!c) return false;
      if (c.startsWith('utilities')) return true;
      // Known aliases that should roll up under Utilities
      const aliases = new Set([
        'water','electric','electricity','electric_gas','electric___gas','recycling','trash','garbage','sewer','utilities__water','utilities__electric','utilities__electric_gas','utilities__other','other_utilities','other'
      ]);
      return aliases.has(c);
    };

    const isHomeLike = (s) => {
      const c = canon(s);
      return !!c && (c === 'home' || c.startsWith('home_'));
    };

    // Mirror backend HOME_TXN_TO_ASSET_CATEGORY mapping to unify keys in charts
    const homeTxnToAssetMap = {
      'home_pest_control': 'pest_control',
      'home_decor': 'home_decor',
      'home_furniture': 'home_furniture',
      'home_artwork': 'home_artwork',
      'home_inspection': 'home_inspection',
      'home_maintenance': 'maintenance',
      'home_repair': 'repair',
      'home_enhancement': 'enhancement',
      'home_improvement': 'enhancement',
      'home_tax_payment': 'taxes',
      'home_insurance_payment': 'insurance',
      'home_plants': 'plants',
      'vehicle_licensing_tags': 'licensing_tags',
    };

    const mapHomeTxnToAsset = (c) => {
      const key = canon(c);
      return homeTxnToAssetMap[key] || null;
    };

    const catKey = (it) => {
      if (showRecurring) {
        return it.recurring ? 'recurring' : 'non_recurring';
      }
      if (useGroups && it.expense_group) {
        return it.expense_group;
      }
      const txnCat = String(it?.txn_category || '');
      const assetType = String(it?.expense_category || ''); // legacy/secondary
      const assetCategory = String(it?.asset_category || ''); // canonical from API when present
      const generic = String(it?.category || '');
      const cTxn = canon(txnCat);
      const cAssetCanonical = canon(assetCategory);
      const cAsset = canon(assetType);
      const cGen = canon(generic);
      // If caller wants asset expense types (e.g., property/vehicle pages), prefer them BUT
      // always collapse Utilities subcategories to the parent 'utilities' when hinted by txn/generic.
      if (preferAssetExpenseTypes) {
        // Normalize Utilities first so they don't get overridden by assetType
        if (isUtilitiesLike(cTxn) || isUtilitiesLike(cGen)) {
          return 'utilities';
        }
        // Prefer canonical asset_category first when available
        if (cAssetCanonical) {
          if (rollupHome && isHomeLike(cAssetCanonical)) return 'home';
          return cAssetCanonical;
        }
        if (cAsset) {
          // Optional roll-up: group Home subcategories as one when toggle is on
          if (rollupHome && isHomeLike(cAsset)) return 'home';
          return cAsset;
        }
        // If no explicit asset category, try mapping Home txn/generic to canonical asset category code
        const mapped = mapHomeTxnToAsset(cTxn) || mapHomeTxnToAsset(cGen);
        if (mapped) {
          if (rollupHome && isHomeLike(mapped)) return 'home';
          return mapped;
        }
        if (cTxn === 'automotive' && cAsset) return cAsset; // safety
        return cGen || cTxn || 'uncategorized';
      }
      // Default: previous behavior prioritizing txn_category, but normalize utilities parent grouping as well
      if (isUtilitiesLike(cTxn) || isUtilitiesLike(cGen)) {
        return 'utilities';
      }
      if (cTxn === 'automotive' && cAsset) return cAsset;
      // Optional roll-up for Home on txn/generic side as a fallback
      const chosen = cTxn || cAsset || cGen || 'uncategorized';
      if (rollupHome && isHomeLike(chosen)) return 'home';
      return chosen;
    };

    (actuals || []).forEach((it) => {
      if (!it?.date || it.amount == null) return;
      const dateKey = moment(it.date).startOf(getPeriod()).format('YYYY-MM-DD');
      const amt = Number(it.amount) || 0;
      actualsMap[dateKey] = (actualsMap[dateKey] || 0) + amt;
      const ck = catKey(it);
      actualBreakdown[dateKey] = actualBreakdown[dateKey] || {};
      actualBreakdown[dateKey][ck] = (actualBreakdown[dateKey][ck] || 0) + amt;
    });

    // Projected recurring expenses (if enabled)
    if (showProjectedRecurring) {
      (projected || []).forEach((it) => {
        // Only include those coming from a recurring series for this specific toggle
        if (!it?.date || it.amount == null || !it.recurring_name) return;
        const dateKey = moment(it.date).startOf(getPeriod()).format('YYYY-MM-DD');
        const amt = Number(it.amount) || 0;
        expectedsMap[dateKey] = (expectedsMap[dateKey] || 0) + amt;
        const ck = catKey(it);
        expectedBreakdown[dateKey] = expectedBreakdown[dateKey] || {};
        expectedBreakdown[dateKey][ck] = (expectedBreakdown[dateKey][ck] || 0) + amt;
      });
    }

    if (showExpected) {
      (projected || []).forEach((it) => {
        // showExpected typically refers to non-recurring expected items (like maintenance schedule)
        // or just all projected by default. But here the user explicitly asked for "Show Projected Recurring".
        // If we have other projected items NOT from recurring, they go here.
        if (!it?.date || it.amount == null || it.recurring_name) return; 
        const dateKey = moment(it.date).startOf(getPeriod()).format('YYYY-MM-DD');
        const amt = Number(it.amount) || 0;
        expectedsMap[dateKey] = (expectedsMap[dateKey] || 0) + amt;
        const ck = catKey(it);
        expectedBreakdown[dateKey] = expectedBreakdown[dateKey] || {};
        expectedBreakdown[dateKey][ck] = (expectedBreakdown[dateKey][ck] || 0) + amt;
      });
    }

    const actualSeries = dateRange.map((d) => actualsMap[d] || 0);
    const expectedSeries = dateRange.map((d) => expectedsMap[d] || 0);

    // Build per-subcategory arrays aligned to dateRange
    const subcatSet = new Set();
    Object.values(actualBreakdown).forEach(m => Object.keys(m).forEach(k => subcatSet.add(k)));
    Object.values(expectedBreakdown).forEach(m => Object.keys(m).forEach(k => subcatSet.add(k)));
    const subcats = Array.from(subcatSet);

    const subBreakActual = {}; // {subcat: [vals per label]}
    const subBreakExpected = {};
    subcats.forEach((cat) => {
      subBreakActual[cat] = dateRange.map(d => (actualBreakdown[d]?.[cat] || 0));
      subBreakExpected[cat] = dateRange.map(d => (expectedBreakdown[d]?.[cat] || 0));
    });

    // Stats calculation (Sum, Avg, StdDev) for Actuals - Only include visible subcats if in stacked/subcat mode
    let actualValues = [];
    const isStacked = showSubcats || useGroups || showRecurring;
    if (isStacked) {
      // For each period, sum only the values of visible categories
      dateRange.forEach(d => {
        let periodSum = 0;
        const breakDown = actualBreakdown[d] || {};
        Object.entries(breakDown).forEach(([cat, val]) => {
          if (!hiddenSubcats.has(cat)) {
            periodSum += Number(val) || 0;
          }
        });
        actualValues.push(periodSum);
      });
    } else {
      // Default: use the full actualSeries if no special grouping is active
      actualValues = hiddenSubcats.has('__ALL_ACTUAL__') ? dateRange.map(() => 0) : actualSeries;
    }

    const sum = actualValues.reduce((a, b) => a + b, 0);
    const avg = actualValues.length > 0 ? sum / actualValues.length : 0;
    const stdDev = actualValues.length > 1
      ? Math.sqrt(actualValues.map(v => Math.pow(v - avg, 2)).reduce((a, b) => a + b, 0) / actualValues.length)
      : 0;

    return { labels: dateRange, actualSeries, expectedSeries, actualBreakdown, expectedBreakdown, subBreakActual, subBreakExpected, subcats, dateRange, stats: { sum, avg, stdDev } };
  }, [actuals, projected, aggregation, fromDate, toDate, useGroups, preferAssetExpenseTypes, showExpected, showProjectedRecurring, showRecurring, hiddenSubcats, showSubcats]);

  useEffect(() => {
    try {
      if (typeof onRangeChange === 'function') {
        onRangeChange(fromDate, toDate);
      }
    } catch (e) { /* noop */ }
  }, [fromDate, toDate, onRangeChange]);

  const formatCurrency = (val) => {
    const n = Number(val) || 0;
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const toTitle = (code) => {
    if (code === 'recurring') return 'Recurring';
    if (code === 'non_recurring') return 'Non-recurring';
    if (code === 'Operating') return 'Operating';
    if (code === 'Financing') return 'Financing';
    if (code === 'CapEx') return 'CapEx';
    const raw = String(code || 'Uncategorized');
    if (raw.startsWith('home_')) {
      const tail = raw.slice('home_'.length).replace(/_/g, ' ');
      const nice = tail.replace(/\b\w/g, c => c.toUpperCase());
      return `Home - ${nice}`;
    }
    const s = raw.replace(/_/g, ' ');
    return s.replace(/\b\w/g, c => c.toUpperCase());
  };

  const cumulate = (arr) => {
    const out = [];
    let run = 0;
    for (let i = 0; i < arr.length; i++) {
      run += Number(arr[i]) || 0;
      out.push(run);
    }
    return out;
  };

  const data = useMemo(() => {
    // Color palette for known sub-categories
    const PALETTE = {
      maintenance: '#60A5FA',
      repair: '#F87171',
      enhancement: '#34D399',
      taxes: '#F59E0B',
      insurance: '#A78BFA',
      ticket_or_fine: '#FB7185',
      asset_input: '#22D3EE',
      asset_storage: '#93C5FD',
      licensing_tags: '#60A5FA',
      initial_purchase: '#4ADE80',
      Operating: '#3B82F6',
      Financing: '#F59E0B',
      CapEx: '#10B981',
      uncategorized: '#9CA3AF',
      recurring: '#3B82F6',
      non_recurring: '#6B7280',
    };
    const fallbackColors = ['#3B82F6','#EF4444','#10B981','#F59E0B','#8B5CF6','#06B6D4','#A3E635','#F472B6','#22C55E','#EAB308'];
    const colorFor = (cat, idx) => PALETTE[cat] || fallbackColors[idx % fallbackColors.length];

    if (showSubcats || useGroups || showRecurring) {
      // Build grouped stacks: one stack for Actual subcats, one for Expected subcats
      const datasets = [];
      const sortedSubcats = [...(subcats || [])].sort();
      sortedSubcats.forEach((cat, i) => {
        const arrA = subBreakActual?.[cat] || [];
        const arrE = showExpected ? (subBreakExpected?.[cat] || []) : [];
        const dsA = cumulative ? cumulate(arrA) : arrA;
        const dsE = cumulative ? cumulate(arrE) : arrE;
        const color = colorFor(cat, i);
        datasets.push({
          label: `Actual • ${toTitle(cat)}`,
          data: dsA,
          backgroundColor: color,
          stack: 'actual',
          order: 1,
        });
        if (showExpected) {
          datasets.push({
            label: `Expected • ${toTitle(cat)}`,
            data: dsE,
            backgroundColor: color + '80', // translucent same color
            stack: 'expected',
            order: 2,
          });
        }
      });
      return { labels, datasets };
    }

    // Default: two simple bars (Actual vs Expected)
    const act = cumulative ? cumulate(actualSeries) : actualSeries;
    const exp = (showExpected || showProjectedRecurring) ? (cumulative ? cumulate(expectedSeries) : expectedSeries) : [];
    return {
      labels,
      datasets: [
        {
          label: 'Actual',
          data: act,
          backgroundColor: 'rgba(59,130,246,0.6)',
          borderColor: 'rgba(59,130,246,1)',
          borderWidth: 1,
          order: 1,
        },
        ...((showExpected || showProjectedRecurring) ? [{
          label: showProjectedRecurring ? 'Projected' : 'Expected',
          data: exp,
          backgroundColor: 'rgba(234,179,8,0.5)',
          borderColor: 'rgba(234,179,8,1)',
          borderWidth: 1,
          order: 2,
        }] : []),
      ],
    };
  }, [labels, actualSeries, expectedSeries, cumulative, showSubcats, useGroups, subcats, subBreakActual, subBreakExpected, showExpected, showProjectedRecurring]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: '#e5e7eb' },
        onClick: (e, legendItem, legend) => {
          const index = legendItem.datasetIndex;
          const ci = legend.chart;
          if (ci.isDatasetVisible(index)) {
            ci.hide(index);
            legendItem.hidden = true;
          } else {
            ci.show(index);
            legendItem.hidden = false;
          }

          // Also update our React state to trigger stats recalculation
          const label = legendItem.text;
          // Legend labels are in format "Actual • CategoryName" or "Expected • CategoryName"
          if (label.includes(' • ')) {
            const catNameRaw = label.split(' • ')[1];
            // We need to find the original category code from subcats by reverse mapping toTitle
            const catCode = subcats.find(c => toTitle(c) === catNameRaw);
            if (catCode) {
              setHiddenSubcats(prev => {
                const next = new Set(prev);
                if (next.has(catCode)) next.delete(catCode);
                else next.add(catCode);
                return next;
              });
            }
          } else if (label === 'Actual') {
             setHiddenSubcats(prev => {
                const next = new Set(prev);
                if (next.has('__ALL_ACTUAL__')) next.delete('__ALL_ACTUAL__');
                else next.add('__ALL_ACTUAL__');
                return next;
             });
          }
        }
      },
      title: { display: false },
      tooltip: {
        backgroundColor: '#111827',
        titleColor: '#e5e7eb',
        bodyColor: '#e5e7eb',
        titleFont: { family: 'monospace' },
        bodyFont: { family: 'monospace' },
        callbacks: {
          title: (items) => {
            if (!items || items.length === 0) return '';
            const i = items[0].dataIndex;
            const label = labels[i] || '';
            // Sum total for this index across only VISIBLE datasets so the Total reflects current legend selections
            let total = 0;
            try {
              const chart = items[0].chart;
              const datasets = chart?.data?.datasets || [];
              datasets.forEach((d, idx) => {
                const meta = chart.getDatasetMeta(idx);
                const visible = !meta?.hidden;
                if (visible) total += Number(d?.data?.[i] || 0);
              });
              // Fallback if something above fails
              if (!isFinite(total) || total === 0) {
                total = Number(items[0].parsed?.y || items[0].raw || 0) || 0;
              }
            } catch (e) {
              total = Number(items[0].parsed?.y || items[0].raw || 0) || 0;
            }
            return `${label} — Total: ${formatCurrency(total)}`;
          },
          afterBody: (items) => {
            try {
              if (!items || items.length === 0) return [];
              const i = items[0].dataIndex;
              const period = labels[i];
              const aMap = actualBreakdown?.[period] || {};
              const eMap = expectedBreakdown?.[period] || {};
              const dsIndex = items[0].datasetIndex ?? 0;
              const chart = items[0].chart;
              const datasets = chart?.data?.datasets || [];

              // Helper: determine if a given sub-category's Actual/Expected dataset is visible
              const labelFor = (cat, kind) => `${kind} • ${toTitle(cat)}`;
              const isVisible = (cat, kind) => {
                const lbl = labelFor(cat, kind);
                const idx = datasets.findIndex(d => String(d?.label||'') === lbl);
                if (idx < 0) return false; // dataset not present
                const meta = chart.getDatasetMeta(idx);
                return !meta?.hidden; // true if visible
              };
              const isStacked = showSubcats || useGroups || showRecurring || cumulative;
              // Helper: sum array up to index i inclusive
              const sumTo = (arr, idx) => {
                let s = 0;
                for (let j = 0; j <= idx && j < arr.length; j++) s += Number(arr[j]) || 0;
                return s;
              };
              // Build the map used for tooltip rows
              let mapToUse = {};
              if (cumulative) {
                // In cumulative mode, show cumulative totals up to index i by sub-category.
                const showCombined = showSubcats || isStacked; // maintain combined behavior when stacked/subcats
                if (showCombined) {
                  // Actual + (optional) Expected cumulative per subcat
                  (subcats || []).forEach((cat) => {
                    let val = 0;
                    const arrA = subBreakActual?.[cat] || [];
                    const arrE = subBreakExpected?.[cat] || [];
                    // Include Actual portion only if its dataset for this cat is visible
                    if (isVisible(cat, 'Actual')) val += sumTo(arrA, i);
                    // Include Expected portion only if showExpected and its dataset is visible
                    if (showExpected && isVisible(cat, 'Expected')) val += sumTo(arrE, i);
                    if (val) mapToUse[cat] = val;
                  });
                } else {
                  // Only the hovered dataset's cumulative per subcat
                  const ds = items[0].chart?.data?.datasets?.[dsIndex];
                  const isExpected = (ds && typeof ds.label === 'string' && ds.label.toLowerCase().startsWith('expected')) || (ds?.stack === 'expected');
                  (subcats || []).forEach((cat) => {
                    const arr = isExpected ? (showExpected ? (subBreakExpected?.[cat] || []) : []) : (subBreakActual?.[cat] || []);
                    const val = sumTo(arr, i);
                    if (val) mapToUse[cat] = val;
                  });
                }
              } else {
                // Non-cumulative mode: use per-period breakdown maps
                const showCombined = showSubcats || isStacked;
                if (showCombined) {
                  const combined = {};
                  // Build combined from visible datasets only
                  (subcats || []).forEach((cat) => {
                    const a = Number(aMap[cat] || 0);
                    const e = Number(eMap[cat] || 0);
                    let val = 0;
                    if (isVisible(cat, 'Actual')) val += a;
                    if (showExpected && isVisible(cat, 'Expected')) val += e;
                    if (val) combined[cat] = val;
                  });
                  mapToUse = combined;
                } else {
                  mapToUse = dsIndex === 0 ? aMap : (showExpected ? eMap : {});
                }
              }
              // Compute total and rows using only visible subcats
              const entriesAll = Object.entries(mapToUse);
              const entriesVisible = entriesAll.filter(([k]) => {
                // In combined mode, keep if either Actual or Expected dataset for this cat is visible
                if (showSubcats || isStacked) {
                  return isVisible(k, 'Actual') || (showExpected && isVisible(k, 'Expected'));
                }
                return true;
              });
              const total = entriesVisible.reduce((s, [,v]) => s + (Number(v)||0), 0);
              if (total <= 0) return [];
              const entries = entriesVisible.sort((a,b) => b[1]-a[1]);
              const nameLens = entries.map(([k]) => toTitle(k).length);
              const valLens = entries.map(([,v]) => formatCurrency(v).length);
              const maxName = nameLens.length ? Math.max(...nameLens) : 0;
              const maxVal = valLens.length ? Math.max(...valLens) : 0;
              const rows = entries.map(([k, v]) => {
                const pct = total ? ((Number(v)/total)*100) : 0;
                const name = toTitle(k).padEnd(maxName, ' ');
                const valStr = formatCurrency(v).padStart(maxVal, ' ');
                const pctStr = `${pct.toFixed(1)}%`;
                return `${name}  ${valStr}  (${pctStr})`;
              });
              return rows;
            } catch (e) {
              return [];
            }
          }
        }
      },
    },
    scales: {
      x: { ticks: { color: '#e5e7eb' }, grid: { color: '#374151' }, stacked: showSubcats || useGroups || showRecurring || cumulative },
      y: { ticks: { color: '#e5e7eb' }, grid: { color: '#374151' }, stacked: showSubcats || useGroups || showRecurring || cumulative },
    },
  }), [labels, data, cumulative, actualBreakdown, expectedBreakdown, showSubcats, useGroups, showRecurring, showExpected, subcats, subBreakActual, subBreakExpected]);

  return (
    <div style={{ height: 400, background: '#0b1220', border: '1px solid #374151', borderRadius: 8, padding: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent:'space-between', marginBottom: 8 }}>
        {/* Left: Title with period/date controls inline to its right */}
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'nowrap', whiteSpace:'nowrap', flex: '1 1 auto', minWidth: 0 }}>
          <div style={{ color: '#e5e7eb', fontWeight: 600, whiteSpace:'nowrap', flexShrink: 0 }}>{chartTitle}</div>
          <select className="form-select form-select-sm" value={aggregation} onChange={(e) => setAggregation(e.target.value)} style={{ width: 120, flexShrink: 0 }}>
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
            <option value="daily">Daily</option>
            <option value="yearly">Yearly</option>
          </select>
          <input className="form-control form-control-sm" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ width: 130, flexShrink: 0 }} />
          <input className="form-control form-control-sm" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ width: 130, flexShrink: 0 }} />
        </div>
        {/* Right: Toggles group */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, fontSize: '12px' }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="form-check form-switch" style={{ display:'flex', alignItems:'center', gap:4 }}>
              <input className="form-check-input" type="checkbox" id="groupSwitch" checked={useGroups} onChange={(e)=> setUseGroups(e.target.checked)} />
              <label className="form-check-label" htmlFor="groupSwitch" style={{ color:'#e5e7eb', whiteSpace: 'nowrap' }}>Show by Type</label>
            </div>
            <div className="form-check form-switch" style={{ display:'flex', alignItems:'center', gap:4 }}>
              <input className="form-check-input" type="checkbox" id="recurringSwitch" checked={showRecurring} onChange={(e)=> setShowRecurring(e.target.checked)} />
              <label className="form-check-label" htmlFor="recurringSwitch" style={{ color:'#e5e7eb', whiteSpace: 'nowrap' }}>Show Recurring</label>
            </div>
            <div className="form-check form-switch" style={{ display:'flex', alignItems:'center', gap:4 }}>
              <input className="form-check-input" type="checkbox" id="subcatSwitch" checked={showSubcats} onChange={(e)=> setShowSubcats(e.target.checked)} />
              <label className="form-check-label" htmlFor="subcatSwitch" style={{ color:'#e5e7eb', whiteSpace: 'nowrap' }}>Show by Sub-Categories</label>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="form-check form-switch" style={{ display:'flex', alignItems:'center', gap:4 }}>
              <input className="form-check-input" type="checkbox" id="cumSwitch" checked={cumulative} onChange={(e)=> setCumulative(e.target.checked)} />
              <label className="form-check-label" htmlFor="cumSwitch" style={{ color:'#e5e7eb', whiteSpace: 'nowrap' }}>Cumulative</label>
            </div>
            <div className="form-check form-switch" style={{ display:'flex', alignItems:'center', gap:4, opacity: 0.5 }}>
              <input className="form-check-input" type="checkbox" id="expectedSwitch" checked={!!showExpected} disabled />
              <label className="form-check-label" htmlFor="expectedSwitch" style={{ color:'#e5e7eb', whiteSpace: 'nowrap' }}>Show Expected</label>
            </div>
            <div className="form-check form-switch" style={{ display:'flex', alignItems:'center', gap:4 }}>
              <input className="form-check-input" type="checkbox" id="projectedRecurringSwitch" checked={showProjectedRecurring} onChange={(e)=> setShowProjectedRecurring(e.target.checked)} />
              <label className="form-check-label" htmlFor="projectedRecurringSwitch" style={{ color:'#e5e7eb', whiteSpace: 'nowrap' }}>Show Projected Recurring</label>
            </div>
            {extraRightControls}
          </div>
        </div>
      </div>
      {stats && (
        <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 16, color: '#9ca3af', fontSize: '13px', marginBottom: 8, paddingLeft: 4 }}>
          <span>Sum: <strong style={{ color: '#e5e7eb' }}>${stats.sum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
          <span>Avg: <strong style={{ color: '#e5e7eb' }}>${stats.avg.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
          <span>Std Dev: <strong style={{ color: '#e5e7eb' }}>${stats.stdDev.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
        </div>
      )}
      {loading ? (
        <div style={{ color: '#9ca3af' }}>Loading…</div>
      ) : error ? (
        <div className="alert alert-warning" style={{ background: 'transparent', color: '#fbbf24' }}>{error}</div>
      ) : (
        <div style={{ height: stats ? 310 : 340 }}>
          <Bar data={data} options={options} height={stats ? 310 : 340} />
        </div>
      )}
    </div>
  );
};

export default AggregatedAssetExpenseChart;
